import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { formatDate } from '@/utils/formatters';
import { kindOf, rangeFor, type RangeKind } from '@/utils/dateRange';
import { Sheet } from './Sheet';
import { Chip } from './Chip';
import { Input } from './Input';
import { Button } from './Button';

export interface DateRange {
  startDate: string; // yyyy-MM-dd
  endDate: string;   // yyyy-MM-dd
}

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}

export function formatDisplayRange(range: DateRange): string {
  if (range.startDate === range.endDate) return formatDate(range.startDate + 'T00:00:00', 'EEEE, dd MMM yyyy');
  return `${formatDate(range.startDate + 'T00:00:00', 'dd MMM')} – ${formatDate(range.endDate + 'T00:00:00', 'dd MMM yyyy')}`;
}

/** Date pill → sheet with preset chips and two native date fields. */
export const DateRangePicker: React.FC<DateRangePickerProps> = ({ value, onChange, className }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>(value);
  const presets: { kind: RangeKind; label: string }[] = [
    { kind: 'today', label: t('dashboard.ranges.today') },
    { kind: 'week', label: t('dashboard.ranges.week') },
    { kind: 'month', label: t('dashboard.ranges.month') },
  ];
  const active = kindOf(draft);

  return (
    <>
      <button
        type="button"
        onClick={() => { setDraft(value); setOpen(true); }}
        className={clsx('flex h-11 items-center gap-2.5 rounded-pill bg-surface-card px-4 text-sm font-extrabold shadow-sm', className)}
      >
        <CalendarDays className="h-[17px] w-[17px] text-ink-brand" />
        <span className="truncate">{formatDisplayRange(value)}</span>
        <ChevronDown className="h-4 w-4 text-ink-muted" />
      </button>
      <Sheet
        isOpen={open}
        onClose={() => setOpen(false)}
        title={t('common.filter')}
        size="sm"
        footer={<Button variant="primary" size="lg" fullWidth onClick={() => { onChange(draft); setOpen(false); }} disabled={draft.startDate > draft.endDate}>{t('common.save')}</Button>}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => <Chip key={p.kind} selected={active === p.kind} onClick={() => setDraft(rangeFor(p.kind))}>{p.label}</Chip>)}
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <Input label={t('common.from')} type="date" value={draft.startDate} max={draft.endDate} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} />
            <Input label={t('common.to')} type="date" value={draft.endDate} min={draft.startDate} onChange={(e) => setDraft({ ...draft, endDate: e.target.value })} />
          </div>
        </div>
      </Sheet>
    </>
  );
};

export default DateRangePicker;
